import { useEffect, useRef } from "react";
import { FormattedMessage, FormattedNumber, useIntl } from "react-intl";
import { Icon } from "./Icon.jsx";

export function Spinner({ size = 18 }) {
  return <span className="spinner" style={{ inlineSize: size, blockSize: size }} aria-hidden="true" />;
}

export function FullPageLoader() {
  return (
    <div className="full-page-loader" role="status" aria-live="polite">
      <Spinner size={28} />
    </div>
  );
}

export function Loading() {
  return (
    <div className="loading-row" role="status">
      <Spinner />
      <FormattedMessage id="common.loading" />
    </div>
  );
}

export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="banner banner-error" role="alert">
      <Icon name="alert" />
      <span className="banner-text">{message}</span>
      {onRetry ? (
        <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
          <FormattedMessage id="common.retry" />
        </button>
      ) : null}
    </div>
  );
}

export function Banner({ kind = "info", icon = "shield", children, action }) {
  return (
    <div className={`banner banner-${kind}`} role={kind === "error" ? "alert" : "status"}>
      <Icon name={icon} />
      <span className="banner-text">{children}</span>
      {action}
    </div>
  );
}

export function EmptyState({ icon = "folder", title, body, action }) {
  return (
    <div className="empty-state">
      <div className="empty-icon">
        <Icon name={icon} size={26} />
      </div>
      {title ? <h3>{title}</h3> : null}
      {body ? <p className="muted">{body}</p> : null}
      {action}
    </div>
  );
}

const STATUS_TONE = {
  queued: "neutral",
  running: "info",
  succeeded: "ok",
  partial: "warn",
  failed: "bad",
  cancelled: "neutral",
  denied: "bad",
  pending: "neutral",
  done: "ok",
  skipped: "neutral",
  open: "ok",
  archived: "neutral",
  accepted: "ok",
  rejected: "bad",
  unreviewed: "neutral",
  approved: "ok",
  active: "ok",
  restricted: "warn",
  suspended: "bad",
  terminated: "bad",
};

/** Status pill. `ns` selects the message namespace (e.g. "jobStatus", "review"). */
export function StatusPill({ ns, value }) {
  const tone = STATUS_TONE[value] || "neutral";
  return (
    <span className={`pill pill-${tone}`}>
      {value === "running" ? <span className="pulse-dot" aria-hidden="true" /> : null}
      <FormattedMessage id={`${ns}.${value}`} defaultMessage={value} />
    </span>
  );
}

export function Credits({ value, compact = false }) {
  if (value === null || value === undefined) return <span className="muted">—</span>;
  return (
    <FormattedNumber value={value} notation={compact ? "compact" : "standard"} maximumFractionDigits={compact ? 1 : 0} />
  );
}

export function Modal({ title, onClose, children, footer, size = "md" }) {
  const intl = useIntl();
  const ref = useRef(null);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    ref.current?.querySelector("input, textarea, select, button")?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose?.()}>
      <div className={`modal modal-${size}`} role="dialog" aria-modal="true" aria-label={title} ref={ref}>
        <header className="modal-head">
          <h2>{title}</h2>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            aria-label={intl.formatMessage({ id: "common.close" })}
          >
            <Icon name="x" />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer ? <footer className="modal-foot">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function ConfidenceBar({ value }) {
  const pct = Math.max(0, Math.min(1, Number(value) || 0));
  const tone = pct >= 0.75 ? "hi" : pct >= 0.4 ? "mid" : "lo";
  return (
    <span className="conf">
      <span className={`conf-track conf-${tone}`}>
        <span className="conf-fill" style={{ inlineSize: `${pct * 100}%` }} />
      </span>
      <span className="conf-num">
        <FormattedNumber value={pct} style="percent" maximumFractionDigits={0} />
      </span>
    </span>
  );
}

export function KeyValue({ data }) {
  const entries = Object.entries(data || {});
  if (!entries.length) return <p className="muted small">—</p>;
  return (
    <dl className="kv">
      {entries.map(([k, v]) => (
        <div className="kv-row" key={k}>
          <dt>{k}</dt>
          <dd className="mono">{typeof v === "object" && v !== null ? JSON.stringify(v) : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}
